import React from 'react';
import { OrganizationProgressItem } from '../../../api/adminOrganizationsService';

interface Props {
  org: OrganizationProgressItem | null;
  selected: string[];
  onChange: (emails: string[]) => void;
}

const InProgressEmployeeSelector: React.FC<Props> = ({ org, selected, onChange }) => {
  if (!org) {
    return (
      <div className="bg-white border rounded-lg p-4 text-sm text-gray-500">
        Selecciona una empresa para ver sus contactos.
      </div>
    );
  }

  const pending = org.employees_pending ?? [];
  // fuerza boolean por si llega null/undefined
  const contactedAll = (org.employees_contacted ?? []).map(c => ({
    ...c,
    reminder_email_sent: Boolean(c.reminder_email_sent),
  }));
  const contactedNoReminder   = contactedAll.filter((c) => !c.reminder_email_sent);
  const contactedWithReminder = contactedAll.filter((c) =>  c.reminder_email_sent);

  const toggle = (email: string) =>
    onChange(
      selected.includes(email)
        ? selected.filter((e) => e !== email)
        : [...selected, email]
    );

  return (
    <div className="bg-white border rounded-lg p-4 max-h-[70vh] overflow-hidden">
      <h4 className="font-semibold mb-3">Contactos</h4>

      {/* Pendientes */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
          Pendientes ({pending.length})
        </div>

        <div className="border rounded max-h-56 overflow-auto">
          <ul className="divide-y">
            {pending.map((e) => (
              <li key={e.email} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm">
                    {e.first_name || e.last_name || e.email}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{e.title}</div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(e.email)}
                    onChange={() => toggle(e.email)}
                  />
                  <span className="text-gray-600">marcar</span>
                </label>
              </li>
            ))}
            {pending.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400">No quedan pendientes.</li>
            )}
          </ul>
        </div>
      </div>

      {/* Ya contactados SIN reminder */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
          Ya contactados (reminder pendiente) ({contactedNoReminder.length})
        </div>

        <div className="border rounded max-h-56 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-gray-600">
                <th className="py-2 pl-3 pr-2">Email</th>
                <th className="py-2 pr-2">Primera toma</th>
                <th className="py-2 pr-3">Seleccionar</th>
              </tr>
            </thead>
            <tbody>
              {contactedNoReminder.map((c) => (
                <tr key={c.email} className="border-t bg-gray-50">
                  <td className="py-2 pl-3 pr-2">
                    <div className="truncate">{c.email}</div>
                    <div className="text-[11px] text-gray-500 truncate">{c.title}</div>
                  </td>
                  <td className="py-2 pr-2">
                    {c.first_contact_at ? new Date(c.first_contact_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.includes(c.email)}
                        onChange={() => toggle(c.email)}
                      />
                      <span className="text-gray-600">marcar</span>
                    </label>
                  </td>
                </tr>
              ))}
              {contactedNoReminder.length === 0 && (
                <tr>
                  <td className="py-2 px-3 text-gray-400" colSpan={3}>—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ya contactados CON reminder */}
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
          Ya contactados (con reminder enviado) ({contactedWithReminder.length})
        </div>

        <div className="border rounded max-h-56 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-gray-600">
                <th className="py-2 pl-3 pr-2">Email</th>
                <th className="py-2 pr-2">Primera toma</th>
                <th className="py-2 pr-3">Reminder</th>
              </tr>
            </thead>
            <tbody>
              {contactedWithReminder.map((c) => (
                <tr key={c.email} className="border-t">
                  <td className="py-2 pl-3 pr-2">
                    <div className="truncate">{c.email}</div>
                    <div className="text-[11px] text-gray-500 truncate">{c.title}</div>
                  </td>
                  <td className="py-2 pr-2">
                    {c.first_contact_at ? new Date(c.first_contact_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 pr-3">✅ enviado</td>
                </tr>
              ))}
              {contactedWithReminder.length === 0 && (
                <tr>
                  <td className="py-2 px-3 text-gray-400" colSpan={3}>—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InProgressEmployeeSelector;
